/** Labels for notification kinds and urgencies, shared by the Notifications and On call settings. */
import { NOTIFY_TRIGGERS } from '@orgus/protocol';
import type { RuleAction, Urgency } from '@orgus/protocol';

export const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'approval.pending', label: 'Approvals and questions' },
  { value: 'session.waiting', label: 'Sessions waiting for input' },
  { value: 'session.ended', label: 'Sessions finished' },
  { value: 'session.unmetered', label: 'Sessions not metered' },
  { value: 'machine.offline', label: 'Machines offline and back' },
  { value: 'pipeline.failed', label: 'Pipeline failures' },
  { value: 'budget.alert', label: 'Budget alerts' },
  { value: 'mission.landed', label: 'Missions landed' },
  { value: 'item.done', label: 'Items done' },
  { value: 'deploy.done', label: 'Deploys finished' },
  { value: 'deploy.rolled-back', label: 'Deploys rolled back' },
  { value: 'escalation', label: 'Escalations' },
  { value: 'digest', label: 'Daily summary' },
  { value: 'batch', label: 'Batched updates' },
  { value: 'test', label: 'Tests' },
];

const KIND_LABEL = new Map(KIND_OPTIONS.map((k) => [k.value, k.label]));
export const kindName = (k: string) => KIND_LABEL.get(k) ?? k;

/** Every trigger is a kind; keep the list honest if a trigger is added. */
export const ALL_KINDS: string[] = [...new Set([...NOTIFY_TRIGGERS, ...KIND_OPTIONS.map((k) => k.value)])];

export const URGENCY_FLOOR_OPTIONS: { value: Urgency; label: string }[] = [
  { value: 'low', label: 'All, low in the digest' },
  { value: 'normal', label: 'Normal and above' },
  { value: 'high', label: 'High and critical' },
  { value: 'critical', label: 'Critical only' },
];

export const RULE_ACTION_OPTIONS: { value: RuleAction; label: string }[] = [
  { value: 'always', label: 'Send now, even in quiet hours' },
  { value: 'digest', label: 'Hold for the summary' },
  { value: 'mute', label: 'Do not send' },
];

/** "Only budget alerts and escalations" style summary of a channel's kinds filter. */
export function kindsSummary(kinds: string[] | null | undefined): string {
  if (!kinds) return 'Everything at or above its urgency';
  if (!kinds.length) return 'Nothing';
  const names = kinds.map(kindName);
  return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}
